import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { debounce } from 'lodash';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  Hidden,
  IconButton,
  Toolbar,
  Button,
} from '@mui/material';
import LogoDashboard from './LogoDashboard';
import GlobalMenu from "./GlobalMenu"
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { logout } from "../utils/auth"
import getInitials from '../utils/getInitials';


import UserContext from "../contexts/UserContext"
import LanguageContext from "../contexts/LanguageContext"

import { useAccesCertification } from "../utils/accesCertification"
import { FenetreCertificationNonDisponible, IconeCadenas } from "./ui/CertificationNonDisponible"


const DashboardNavbar = ({ ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    actLanguage,
    languageValues,
  } = useContext(LanguageContext)

  const {
    updateUserContext,
    currentUser,
  } = useContext(UserContext)


  const globalMenuRef = useRef(null)
  const [globalMenu, setGlobalMenu] = useState(false)

  /* ⛔ ACCES A « MA CERTIFICATION » (06/10/2026, regle de Nawal, detail dans
     utils/accesCertification.js). Etablissement sans certification : l'onglet
     reste VISIBLE, grise, avec un cadenas ; un clic ouvre le message au lieu
     de mener au contenu.
     Pendant le chargement, l'onglet garde sa place mais reste INVISIBLE, puis
     apparait directement dans son etat final : ni « actif puis grise », ni
     decalage du menu. Le statut est memorise pour la session, il ne se
     recharge pas a chaque retour au tableau de bord. */
  const statutCertif = useAccesCertification(props?.menuforadmin !== "true")
  const certifVerrouillee = statutCertif === "inactive"
  const certifEnAttente = statutCertif === "chargement"
  const [fenetreCertif, setFenetreCertif] = useState(false)
  /* `handleOutsideClick` est pose UNE fois, au montage : il ne verrait que
     l'etat du premier rendu. Il lit donc le verrou dans une reference. */
  const certifVerrouilleeRef = useRef(certifVerrouillee)
  certifVerrouilleeRef.current = certifVerrouillee

  const handleOutsideClick = (e) => {
    if (globalMenuRef.current && !globalMenuRef.current.contains(e.target)) {
      setGlobalMenu(false)
    }
    if (observedElementRef.current && !observedElementRef.current.contains(e.target)) {
      if (e?.target?.classList?.contains("customForClickMenuMobile")) {
        e?.target?.classList?.forEach((classe) => {
          if (classe?.includes("goToPage-")) {
            let pageID = classe?.replace("goToPage-", "")
            if (props?.menuforadmin === "true") {
              //forAdminMenu
              let find = itemsMenuAdmin?.find((el) => el.page === pageID)
              if (find) {
                goToThisPage('/admin/'+find?.page)
              }
            } else {
              //for classicMenu
              let find = itemsMenu?.find((el) => el.page === pageID)
              if (find?.page === 'myCertification' && certifVerrouilleeRef.current) {
                setFenetreCertif(true)
              } else if (find) {
                goToThisPage('/'+find?.page)
              }
            }
          }
        })
      }
      setMenuMobileDropdown(false)
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const itemsMenuAdmin = [
    {
      page: 'groupe',
      title: "Groupes",
    },
    {
      page: 'formation',
      title: "Modules",
    },
    {
      page: 'ecoles',
      title: "Écoles",
    },
    {
      page: 'adminclient',
      title: "Admin Clients",
    },
    {
      page: 'support',
      title: "Support",
    },
  ];

  const itemsMenu = [
    {
      page: 'dashboard',
      title: languageValues?.studentMenu?.dashboard[actLanguage],
    },
    {
      page: 'myCertification',
      title: languageValues?.studentMenu?.myCertif[actLanguage],
      verrouille: certifVerrouillee,
      enAttente: certifEnAttente,
    },
  ];

  const [menuMobileDropdown, setMenuMobileDropdown] = useState(false)
  const theme = useTheme();
  const mediaQueryUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const mediaQueryUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const observedElementRef = useRef(null);
  const [leftOffsetForMobileMenu, setLeftOffsetForMobileMenu] = useState(99999)
  const [widthForMobileMenu, setWidthForMobileMenu] = useState(0)
  useEffect(() => {
    if (observedElementRef.current) {
      const observer = new ResizeObserver(debounce((entries) => {
        for (let entry of entries) {
          setLeftOffsetForMobileMenu(observedElementRef?.current?.getBoundingClientRect()?.left - 11)
          setWidthForMobileMenu(observedElementRef?.current?.getBoundingClientRect()?.width + 11)
          return
        }
      }));
      observer.observe(observedElementRef?.current);
      return () => {
          observer.disconnect();
      };
    }
  }, [observedElementRef?.current, mediaQueryUpMd, mediaQueryUpSm]);

  useEffect(() => {
    if (mediaQueryUpMd === true && menuMobileDropdown === true) {
      setMenuMobileDropdown(false)
    }
  }, [mediaQueryUpMd, menuMobileDropdown])

  const getActPageForMenuMobileAdmin = () => {
    let split = window?.location?.pathname?.split('/')
    split = split.filter((el) => el !== '')
    let find = itemsMenuAdmin?.find((el) => el.page === split[1])
    if (find) {
      return find?.title
    }
    return ""
  }

  const getActPageForMenuMobile = () => {
    let split = window?.location?.pathname?.split('/')
    split = split.filter((el) => el !== '')
    let find = itemsMenu?.find((el) => el.page === split[0])
    if (find) {
      return find?.title
    }
    return ""
  }

  const goToThisPage = (page) => {
    navigate(page)
  }


  return (
    <AppBar
      elevation={0}
      sx={{background:"#FFFFFF", borderBottom:"1px solid black"}}
      {...props}
    >
      <Toolbar
        sx={{
          height: ["55px !important", "65px !important"],
          minHeight: ["55px !important", "65px !important"],
          background:"#FFFFFF",
        }}>
        <Box sx={{
          display:"flex",
          alignItems:"center",
          height:"100%",
          maxWidth:"calc(100vw - 100px)",
        }}>
          <Box sx={{
            marginLeft:"5px",
            marginRight:["0px", "25px"],
            paddingRight:["15px", "0px"],
            display:"flex",
            alignItems:"center",
            cursor:"pointer",
            borderRight:["2px solid #E6E6E7", "0"],
          }} onClick={() => navigate("/")}>
            <LogoDashboard />
          </Box>

            {props?.menuforadmin === "true" ? (
              <>
                <Hidden mdDown>
                  <Box sx={{display:"flex", marginLeft:"25px", marginRight:"25px", justifyContent:"space-between", gap:"30px"}}>
                    {itemsMenuAdmin.map((item, index) => {
                      let active = false
                      let split = window?.location?.pathname?.split('/')
                      split = split.filter((el) => el !== '')
                      if (split[1] === item?.page) {
                        active = true
                      }
                      return (
                        <Button
                          key={index}
                          disableRipple
                          onClick={() => {
                            goToThisPage('/admin/'+item?.page)
                          }}
                          sx={{
                            fontSize:"16px",
                            color:"text.secondary",
                            textDecoration:active && "underline",
                            textUnderlineOffset: "6px",
                            minWidth:0,
                            "&:hover" : {
                              background:"transparent",
                              color:"rgba(0,0,0,0.75)",
                              textDecoration:active && "underline",
                            }
                          }}
                          size="small"
                        >
                          <span>
                            {item?.title}
                          </span>
                        </Button>
                      )
                    })}

                  </Box>
                </Hidden>
                <Hidden mdUp>
                  <Box ref={observedElementRef} sx={{display:"flex", marginLeft:"10px", marginRight:"0px", justifyContent:"space-between", gap:"30px", overflow:"hidden"}}>
                    <Box
                      onClick={() => { setMenuMobileDropdown(!menuMobileDropdown) }}
                      sx={{
                        display:"flex",
                        height:"100%",
                        alignItems:"center",
                        padding:"15px",
                        overflow:"hidden",
                        cursor:"pointer",
                        gap:"8px",
                        color:"text.secondary",
                      }}
                    >
                      <span style={{
                        textTransform:"capitalize",
                        whiteSpace:"nowrap",
                        overflow:"hidden",
                        textOverflow:"ellipsis",
                      }}>{getActPageForMenuMobileAdmin()}</span>
                      <KeyboardArrowDownIcon sx={{
                        transform:menuMobileDropdown === true && "rotate(180deg)",
                      }}/>
                    </Box>
                  </Box>
                  {menuMobileDropdown === true &&
                    <Box sx={{
                      width:widthForMobileMenu+"px",
                      position: "absolute",
                      top:["56px", "66px"],
                      left:leftOffsetForMobileMenu+"px",
                      borderLeft:"1px solid black",
                      borderRight:"1px solid black",
                      borderBottom:"1px solid black",
                      boxShadow: "0px 1px 20px rgba(0, 0, 0, 0.1)",
                      cursor:"pointer",
                      display:"flex",
                      flexDirection:"column",
                      overflow:"hidden",
                      background:"white",
                    }}>
                      {itemsMenuAdmin.map((item, index) => {
                        let active = false
                        let split = window?.location?.pathname?.split('/')
                        split = split.filter((el) => el !== '')
                        if (split[1] === item?.page) {
                          active = true
                        }
                        return (
                          <Button
                            key={index}
                            className={"customForClickMenuMobile goToPage-"+item?.page}
                            disableRipple
                            sx={{
                              height:'50px !important',
                              minHeight:'50px !important',
                              fontSize:"15px !important",
                              color:"text.secondary",
                              textDecoration:active && "underline",
                              textUnderlineOffset: "6px",
                              minWidth:0,
                              "&:hover" : {
                                background:"transparent",
                                color:"rgba(0,0,0,0.75)",
                                textDecoration:active && "underline",
                              }
                            }}
                            size="small"
                          >
                            <span className={"customForClickMenuMobile goToPage-"+item?.page}>
                              {item?.title}
                            </span>
                          </Button>
                        )
                      })}
                    </Box>
                  }
                </Hidden>
              </>
            ) : (
              <>
                <Hidden mdDown>
                  <Box sx={{display:"flex", marginLeft:"25px", marginRight:"25px", justifyContent:"space-between", gap:"30px"}}>
                    {itemsMenu.map((item, index) => {
                      let active = false
                      let split = window?.location?.pathname?.split('/')
                      split = split.filter((el) => el !== '')
                      if (split[0] === item?.page) {
                        active = true
                      }
                      const verrouille = item?.verrouille === true
                      const enAttente = item?.enAttente === true
                      return (
                        <Button
                          key={index}
                          disableRipple
                          /* Etiquette stable pour la visite guidee : une classe
                             MUI generee changerait au prochain build.
                             06/10/2026 : retiree quand l'onglet est verrouille
                             ou en attente. La visite saute une etape sans
                             cible ; elle ne dira pas « vous les retrouvez ici »
                             en montrant un onglet ferme. */
                          data-visite={(verrouille || enAttente) ? undefined : "menu-" + item?.page}
                          data-verrouille={verrouille ? "true" : undefined}
                          aria-disabled={verrouille ? "true" : undefined}
                          aria-hidden={enAttente ? "true" : undefined}
                          tabIndex={enAttente ? -1 : undefined}
                          onClick={() => {
                            if (verrouille) {
                              setFenetreCertif(true)
                            } else {
                              goToThisPage('/'+item?.page)
                            }
                          }}
                          sx={{
                            fontSize:"16px",
                            color:verrouille ? "#8A8480" : "text.secondary",
                            fontWeight:active ? 600 : 400, // L25 : actif sans soulignement
                            minWidth:0,
                            visibility:enAttente ? "hidden" : "visible",
                            "&:hover" : {
                              background:"transparent",
                              color:verrouille ? "#8A8480" : "rgba(0,0,0,0.75)",
                            }
                          }}
                          size="small"
                        >
                          <span style={verrouille ? {display:"inline-flex", alignItems:"center", gap:"7px"} : undefined}>
                            {item?.title}
                            {verrouille && <IconeCadenas taille={12} />}
                          </span>
                        </Button>
                      )
                    })}

                  </Box>
                </Hidden>
                <Hidden mdUp>
                  <Box ref={observedElementRef} sx={{display:"flex", marginLeft:"10px", marginRight:"0px", justifyContent:"space-between", gap:"30px", overflow:"hidden"}}>
                    <Box
                      onClick={() => { setMenuMobileDropdown(!menuMobileDropdown) }}
                      sx={{
                        display:"flex",
                        height:"100%",
                        alignItems:"center",
                        padding:"15px",
                        overflow:"hidden",
                        cursor:"pointer",
                        gap:"8px",
                        color:"text.secondary",
                      }}
                    >
                      <span style={{
                        textTransform:"capitalize",
                        whiteSpace:"nowrap",
                        overflow:"hidden",
                        textOverflow:"ellipsis",
                      }}>{getActPageForMenuMobile()}</span>
                      <KeyboardArrowDownIcon sx={{
                        transform:menuMobileDropdown === true && "rotate(180deg)",
                      }}/>
                    </Box>
                  </Box>
                  {menuMobileDropdown === true &&
                    <Box sx={{
                      width:widthForMobileMenu+"px",
                      position: "absolute",
                      top:["56px", "66px"],
                      left:leftOffsetForMobileMenu+"px",
                      borderLeft:"1px solid black",
                      borderRight:"1px solid black",
                      borderBottom:"1px solid black",
                      boxShadow: "0px 1px 20px rgba(0, 0, 0, 0.1)",
                      cursor:"pointer",
                      display:"flex",
                      flexDirection:"column",
                      overflow:"hidden",
                      background:"white",
                    }}>
                      {itemsMenu.map((item, index) => {
                        let active = false
                        let split = window?.location?.pathname?.split('/')
                        split = split.filter((el) => el !== '')
                        if (split[0] === item?.page) {
                          active = true
                        }
                        const verrouille = item?.verrouille === true
                        const enAttente = item?.enAttente === true
                        return (
                          <Button
                            key={index}
                            className={"customForClickMenuMobile goToPage-"+item?.page}
                            disableRipple
                            data-verrouille={verrouille ? "true" : undefined}
                            aria-disabled={verrouille ? "true" : undefined}
                            aria-hidden={enAttente ? "true" : undefined}
                            tabIndex={enAttente ? -1 : undefined}
                            sx={{
                              height:'50px !important',
                              minHeight:'50px !important',
                              fontSize:"15px !important",
                              color:verrouille ? "#8A8480" : "text.secondary",
                              fontWeight:active ? 600 : 400, // L25
                              minWidth:0,
                              visibility:enAttente ? "hidden" : "visible",
                              "&:hover" : {
                                background:"transparent",
                                color:verrouille ? "#8A8480" : "rgba(0,0,0,0.75)",
                              }
                            }}
                            size="small"
                          >
                            <span className={"customForClickMenuMobile goToPage-"+item?.page} style={verrouille ? {display:"inline-flex", alignItems:"center", gap:"7px"} : undefined}>
                              {item?.title}
                              {/* le clic doit tomber sur le libelle, qui porte
                                  la classe lue par handleOutsideClick */}
                              {verrouille && <IconeCadenas taille={12} style={{pointerEvents:"none"}} />}
                            </span>
                          </Button>
                        )
                      })}
                    </Box>
                  }
                </Hidden>
              </>
            )}

        </Box>
        <Box sx={{ flexGrow: 1 }}>
        </Box>


        <GlobalMenu
          globalMenu={globalMenu}
          setGlobalMenu={setGlobalMenu}
          globalMenuRef={globalMenuRef}
          updateUserContext={updateUserContext}
          currentUser={currentUser}
        />

        <FenetreCertificationNonDisponible
          ouverte={fenetreCertif}
          onFermer={() => { setFenetreCertif(false) }}
        />


      </Toolbar>
    </AppBar>
  );
};


export default DashboardNavbar;
